import React, { FC, useCallback, useEffect } from 'react';
import { Search } from '@saucelabs/ds-inputs';
import { Filter, FilterBar } from '@saucelabs/ds-filters';
import {
  IconDeviceDesktopOutline,
  IconDevicePhoneOutline,
  IconOsOutline,
  IconRefreshFill,
  IconThumbsUpOutline,
} from '@saucelabs/ds-icons';
import { Item } from 'react-stately';
import { IconButton } from '@saucelabs/ds-button';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { Typography } from '@saucelabs/ds-typography';
import { Badge } from '@saucelabs/ds-badge';
import { debounce } from 'lodash';

import { useVisualDispatch, useVisualSelector } from '../../reduxHooks';
import { actions, constants, selectors, thunks, types } from '../../ducks/buildDetailsFilterBar';
import {
  actions as buildDetailsActions,
  selectors as buildDetailsSelectors,
} from '../../ducks/buildDetails';

const renderFilterBarItem = (item: types.FilterItem) => (
  <Item
    key={item.key}
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    prefixIcon={item.prefixIcon}
  >
    {item.label}
  </Item>
);

const OVERLAY_PROP_DEFAULTS = {
  style: {
    width: 230,
  },
};

export const BuildDetailsFilterBar: FC<{onReload: () => void;}> = (props) => {
  const { onReload } = props;
  const filterBar = useVisualSelector(selectors.buildDetailsFilterBar);
  const searchParams = useVisualSelector(selectors.getSearchParams);
  const dispatch = useVisualDispatch();
  const { init } = filterBar;

  const setFilterValue = useCallback((
    key: Exclude<keyof types.FilterBarState, 'search' | 'init' | 'project' | 'userUpdated'>,
  ) => (value: types.Selection) => {
    dispatch(actions.setFilterValue({
      key,
      value,
    }));
  }, [dispatch]);

  const onSearchChange = useCallback((value: string) => {
    dispatch(actions.setSearchValue(value));
    void dispatch(thunks.updateSearchField(value));
  }, [dispatch]);

  const onReset = useCallback(() => {
    dispatch(actions.clearFilters());
  }, [dispatch]);

  const { haveItemsChanged } = useVisualSelector(buildDetailsSelectors.root);
  const onRefresh = useCallback(() => {
    onReload();
    dispatch(buildDetailsActions.setItemsChanged(false));
  }, [dispatch, onReload]);

  const saveFilters = useCallback(debounce(() => {
    void dispatch(thunks.saveFilters());
  }, 300, { trailing: true }), [dispatch]);

  useEffect(() => {
    // Only update / save filters after the initialization is done (so we don't overwrite values
    // if they haven't been parsed out of the query string yet)
    if (init === 'succeeded') {
      saveFilters();
    }
  }, [saveFilters, searchParams, init]);

  const filtersDisabled = init !== 'succeeded';

  return (
    <div className="p-2 flex flex-row flex-wrap items-end gap-1">
      <div className="max-w-34.75 min-w-0 pb-0.5">
        <Search
          onChange={onSearchChange}
          value={filterBar.search.inputValue}
        />
      </div>
      <div className="pb-0.5">
        <Typography
          className="mb-0.5"
          variant="overline"
        >
          Group By
        </Typography>
        <Filter
          label="Group By"
          name="groupby"
          onSelectionChange={setFilterValue('groupBy')}
          overlayProps={OVERLAY_PROP_DEFAULTS}
          prefixIcon={<></>}
          selectedKeys={filterBar.groupBy.value}
          selectionMode="multiple"
        >
          {filterBar.groupBy.items.map((item: types.FilterItem<constants.DetailsGroupBy>) => {
            const index = filterBar.groupBy.value.indexOf(item.key) + 1;
            return (
              <Item
                key={item.key}
                textValue={item.label}
              >
                <span className="whitespace-nowrap flex flex-row gap-1">
                  <span>{item.label}</span>
                  {index > 0 && (
                    <Badge
                      className="ml-auto min-w-3 text-center"
                      color="orange"
                      emphasis="low"
                    >
                      {index > 0 ? index : null}
                    </Badge>
                  )}
                </span>
              </Item>
            );
          })}
        </Filter>
      </div>
      <FilterBar
        onReset={onReset}
        showResetButton
      >
        {/* I know this is hacky, but the FilterBar requires multiple children to enable the
        reset button. It blocks state management from FilterBar */}
        <></>
        <>
          <Filter
            isDisabled={filterBar.os.items.length < 1 || filtersDisabled}
            items={filterBar.os.items}
            label="OS"
            name="os"
            onSelectionChange={setFilterValue('os')}
            prefixIcon={IconOsOutline}
            selectedKeys={filterBar.os.value}
            selectionMode="multiple"
          >
            {renderFilterBarItem}
          </Filter>
          <Filter
            isDisabled={filterBar.browser.items.length < 1 || filtersDisabled}
            items={filterBar.browser.items}
            label="Browser"
            name="browser"
            onSelectionChange={setFilterValue('browser')}
            prefixIcon={IconDeviceDesktopOutline}
            selectedKeys={filterBar.browser.value}
            selectionMode="multiple"
          >
            {renderFilterBarItem}
          </Filter>
          <Filter
            isDisabled={filterBar.device.items.length < 1 || filtersDisabled}
            items={filterBar.device.items}
            label="Device"
            name="device"
            onSelectionChange={setFilterValue('device')}
            prefixIcon={IconDevicePhoneOutline}
            selectedKeys={filterBar.device.value}
            selectionMode="multiple"
          >
            {renderFilterBarItem}
          </Filter>
          <Filter
            items={filterBar.status.items}
            label="Status"
            name="status"
            onSelectionChange={setFilterValue('status')}
            prefixIcon={IconThumbsUpOutline}
            selectedKeys={filterBar.status.value}
            selectionMode="multiple"
          >
            {renderFilterBarItem}
          </Filter>
        </>
      </FilterBar>
      {haveItemsChanged && (
        <Tooltip title="Items have changed since filters were applied. Reload?">
          <IconButton
            className="mb-0.5"
            icon={<IconRefreshFill className="w-2 h-2" />}
            onPress={onRefresh}
            variant="text"
          />
        </Tooltip>
      )}
    </div>
  );
};
